import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SeeItInAction } from "@/components/SeeItInAction";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PMBOK_GROUPS, PRIORITIES } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LibraryResources from "@/components/LibraryResources";
import ArchivedProjects from "@/components/ArchivedProjects";
import { toast } from "sonner";
import { Plus, Loader2, Search, Library as LibraryIcon, Flag, ListTodo, Trash2, BookMarked } from "lucide-react";

const blank = { title: "", description: "", kind: "task", phase: "Planning", knowledge_area: "", priority: "medium", assignee: "", default_effort_days: "", tags: [] };

const LIB_TABS = ["items", "resources", "templates", "archived"];

export default function LibraryPage() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(LIB_TABS.includes(sp.get("tab")) ? sp.get("tab") : "items");
  useEffect(() => { const t = sp.get("tab"); if (t && LIB_TABS.includes(t)) setTab(t); }, [sp]);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (kind !== "all") params.set("kind", kind);
    const { data } = await api.get(`/library?${params.toString()}`);
    setItems(data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...blank, ...it, tags: it.tags || [] }); setEditId(it.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, default_effort_days: form.default_effort_days === "" ? null : Number(form.default_effort_days), tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
      if (editId) await api.put(`/library/${editId}`, payload);
      else await api.post(`/library`, payload);
      setOpen(false);
      toast.success(editId ? "Updated" : "Added to library");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/library/${id}`); setOpen(false); toast.success("Removed"); load(); };

  return (
    <Layout>
      <div className="mb-8">
        <p className="overline text-muted-foreground mb-2">Reusable building blocks</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Library</h1>
        <p className="text-muted-foreground mt-2">Save proven tasks, milestones and resources once, then drop them into any future plan.</p>
        <SeeItInAction chapter={15} className="mt-2" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="library-tabs">
          <TabsTrigger value="items" data-testid="library-tab-items">Tasks & Milestones</TabsTrigger>
          <TabsTrigger value="resources" data-testid="library-tab-resources">Resources</TabsTrigger>
          <TabsTrigger value="templates" data-testid="library-tab-templates">Project Templates</TabsTrigger>
          <TabsTrigger value="archived" data-testid="library-tab-archived">Archived Projects</TabsTrigger>
        </TabsList>

        <TabsContent value="archived" className="mt-6">
          <ArchivedProjects />
        </TabsContent>

        <TabsContent value="items" className="mt-6">
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, tag or text…" className="pl-9" data-testid="library-search-input" />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-[160px]" data-testid="library-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All items</SelectItem>
            <SelectItem value="task">Tasks</SelectItem>
            <SelectItem value="milestone">Milestones</SelectItem>
          </SelectContent>
        </Select>
        <Button className="gap-2" onClick={openNew} data-testid="library-add-button"><Plus className="h-4 w-4" /> New item</Button>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="library-empty">
          <LibraryIcon className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Your library is empty. Add reusable tasks & milestones, or use "Save to library" from any task.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
          {items.map((it) => (
            <button key={it.id} onClick={() => openEdit(it)} className="text-left border border-border rounded-lg p-5 hover:-translate-y-1 hover:shadow-lg hover:border-foreground/30 transition-all bg-card" data-testid={`library-item-${it.id}`}>
              <div className="flex items-center gap-2 mb-2">
                {it.kind === "milestone" ? <Flag className="h-4 w-4 text-primary" /> : <ListTodo className="h-4 w-4 text-muted-foreground" />}
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{it.kind}</span>
              </div>
              <h3 className="font-display font-bold tracking-tight">{it.title}</h3>
              {it.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className="text-[11px] px-2 py-0.5 rounded bg-secondary">{it.phase}</span>
                {(it.tags || []).map((tg) => <span key={tg} className="text-[11px] px-2 py-0.5 rounded bg-secondary text-muted-foreground">#{tg}</span>)}
              </div>
            </button>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="resources" className="mt-6">
          <LibraryResources />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <ProjectTemplates />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="library-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "New"} library item</DialogTitle>
            <DialogDescription>Reusable across all your projects.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="l-title">Title</Label>
              <Input id="l-title" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="library-title-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="library-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="task">Task</SelectItem><SelectItem value="milestone">Milestone</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="l-phase">Default phase</Label>
                <Input id="l-phase" value={form.phase} onChange={(e) => set("phase", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="l-eff">Default effort (days)</Label>
                <Input id="l-eff" type="number" value={form.default_effort_days ?? ""} onChange={(e) => set("default_effort_days", e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="l-assignee">Default assignee / role</Label>
              <Input id="l-assignee" value={form.assignee} onChange={(e) => set("assignee", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="l-desc">Description</Label>
              <Textarea id="l-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="l-tags">Tags (comma-separated)</Label>
              <Input id="l-tags" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="governance, startup" data-testid="library-tags-input" className="mt-1.5" />
            </div>
            <DialogFooter className="flex sm:justify-between gap-2">
              {editId && <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => remove(editId)} data-testid="library-delete-button"><Trash2 className="h-4 w-4" /> Delete</Button>}
              <Button type="submit" disabled={saving} data-testid="library-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ProjectTemplates() {
  const [templates, setTemplates] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/templates"); setTemplates(data); }
    catch { setTemplates([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/templates/${id}`); toast.success("Template deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  if (templates === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!templates.length) return (
    <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="templates-empty">
      <BookMarked className="h-8 w-8 text-primary mx-auto mb-3" />
      <p className="text-muted-foreground">No saved templates yet. Open any project and use <b>"Save as template"</b> to turn it into a reusable playbook.</p>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
      {templates.map((t) => (
        <div key={t.id} className="border border-border rounded-lg p-5 bg-card flex flex-col" data-testid={`template-card-${t.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <BookMarked className="h-4 w-4 text-primary" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t.base_methodology || "Custom"} template</span>
          </div>
          <h3 className="font-display font-bold tracking-tight">{t.name}</h3>
          {t.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.description}</p>}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <span className="text-[11px] px-2 py-0.5 rounded bg-secondary">{t.task_count} tasks</span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-secondary text-muted-foreground">{(t.phases || []).length} phases</span>
          </div>
          <div className="mt-4 pt-3 border-t border-border flex justify-end">
            <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-1.5" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
